import { computed, useHost, onBeforeUnmount, type Ref, type ComputedRef } from "vue";
import type { ComboboxInputProps } from "./combobox.types";
import { useIcons } from "composables/useIcons";
import { useCombobox, getItemLabel, registerComboboxInputElement, unregisterComboboxInputElement } from "./useCombobox";
import { comboboxInputClasses } from "./combobox.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { useTagReorder, type ReorderTagBindings, type ReorderContainerBindings } from "composables/useTagReorder";
import { useKeyboardNavigation } from "composables/useKeyboardNavigation";
import type { AcceptableValue } from "@/runtime/types/utils";

const BLUR_CLOSE_DELAY_MS = 150;

export interface UseComboboxInputOptions {
  props: Readonly<ComboboxInputProps>;
  inputRef: Ref<HTMLInputElement | null>;
}

export interface UseComboboxInputReturn {
  isLeading: ComputedRef<boolean>;
  leadingIconName: ComputedRef<string | undefined>;
  isMultiple: ComputedRef<boolean>;
  tags: ComputedRef<AcceptableValue[]>;
  getTagDisplayValue: (value: AcceptableValue) => string;
  removeTag: (value: AcceptableValue) => void;
  placeholder: ComputedRef<string>;
  inputValue: ComputedRef<string>;
  classes: ComputedRef<ReturnType<typeof comboboxInputClasses>>;
  tagBindings: (index: number) => ReorderTagBindings;
  reorderContainerBindings: () => ReorderContainerBindings;
  combobox: ReturnType<typeof useCombobox>;
  handleInput: (event: Event) => void;
  handleFocus: () => void;
  handleBlur: (event: FocusEvent) => void;
  handleKeydown: (event: KeyboardEvent) => void;
}

/** Build computed properties for combobox input. */
const buildInputComputeds = (props: Readonly<ComboboxInputProps>, combobox: ReturnType<typeof useCombobox>) => {
  const isMultiple = computed(() => combobox.comboboxProps.value?.multiple ?? false);
  const tags = computed((): AcceptableValue[] => {
    if (!isMultiple.value) return [];
    const value = combobox.modelValue.value;
    return Array.isArray(value) ? value : [];
  });
  const placeholder = computed(() => props.placeholder ?? combobox.comboboxProps.value?.placeholder ?? "Search...");
  const inputValue = computed(() => {
    if (combobox.isOpen.value || combobox.searchTerm.value) return combobox.searchTerm.value;
    if (isMultiple.value) return "";
    const selected = combobox.modelValue.value;
    if (selected === undefined || selected === null) return "";
    if (props.displayValue) return props.displayValue(selected as AcceptableValue);
    return combobox.displayValue.value;
  });
  const classes = computed(() =>
    mergeClassProp(
      comboboxInputClasses({
        color: combobox.comboboxProps.value?.color,
        variant: combobox.comboboxProps.value?.variant,
        size: combobox.comboboxProps.value?.size,
        open: combobox.isOpen.value ? "true" : "false",
        disabled: combobox.comboboxProps.value?.disabled ? "true" : "false",
        multiple: isMultiple.value ? "true" : "false",
        block: combobox.comboboxProps.value?.block ? "true" : "false",
      }),
      props.class,
    ),
  );
  return { isMultiple, tags, placeholder, inputValue, classes };
};

/** Handle keydown when combobox is closed. Returns true if handled. */
const handleClosedKeydown = (event: KeyboardEvent, combobox: ReturnType<typeof useCombobox>): boolean => {
  if (["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) {
    event.preventDefault();
    event.stopPropagation();
    combobox.open();
    return true;
  }
  return event.key === "Home" || event.key === "End";
};

/** Handle backspace for tag removal. Returns true if handled. */
const handleBackspaceTag = (
  event: KeyboardEvent,
  isMultiple: ComputedRef<boolean>,
  tags: ComputedRef<AcceptableValue[]>,
  removeTag: (value: AcceptableValue) => void,
): boolean => {
  if (event.key !== "Backspace") return false;
  const target = event.target as HTMLInputElement;
  if (isMultiple.value && !target.value && tags.value.length > 0) {
    removeTag(tags.value.at(-1));
  }
  return true;
};

/** Handle keydown events for combobox input. */
const handleComboboxKeydown = (
  event: KeyboardEvent,
  combobox: ReturnType<typeof useCombobox>,
  isMultiple: ComputedRef<boolean>,
  tags: ComputedRef<AcceptableValue[]>,
  removeTag: (value: AcceptableValue) => void,
  navKeydown: (event: KeyboardEvent) => void,
): void => {
  if (combobox.comboboxProps.value?.disabled) return;
  if (!combobox.isOpen.value && handleClosedKeydown(event, combobox)) return;
  if (handleBackspaceTag(event, isMultiple, tags, removeTag)) return;
  navKeydown(event);
  if (event.defaultPrevented) event.stopPropagation();
};

/** Resolve the display value for a tag. */
const resolveTagDisplayValue = (
  value: AcceptableValue,
  props: Readonly<ComboboxInputProps>,
  combobox: ReturnType<typeof useCombobox>,
): string => {
  if (props.displayValue) return props.displayValue(value);
  const comboboxEl = combobox.comboboxElement.value;
  if (comboboxEl) {
    const label = getItemLabel(comboboxEl, value);
    if (label) return label;
  }
  return String(value);
};

/** Build event handlers for combobox input. */
const buildComboboxInputHandlers = (
  combobox: ReturnType<typeof useCombobox>,
  blurState: { timeoutId: ReturnType<typeof setTimeout> | null },
) => {
  const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    combobox.setSearchTerm(target.value);
    if (!combobox.isOpen.value && target.value) combobox.open();
    combobox.setHighlightedValue(combobox.filteredItems.value[0]);
  };
  const handleFocus = () => {
    if (combobox.comboboxProps.value?.disabled) return;
    if (combobox.comboboxProps.value?.openOnFocus !== false) combobox.open();
  };
  const handleBlur = (event: FocusEvent) => {
    const relatedTarget = event.relatedTarget as Element | null;
    if (relatedTarget && combobox.comboboxElement.value?.contains(relatedTarget)) return;
    if (blurState.timeoutId) clearTimeout(blurState.timeoutId);
    blurState.timeoutId = setTimeout(() => {
      combobox.close();
      blurState.timeoutId = null;
    }, BLUR_CLOSE_DELAY_MS);
  };
  return { handleInput, handleFocus, handleBlur };
};

/** Wire arrow/enter/escape/tab navigation against the combobox model. */
const buildNavKeydown = (combobox: ReturnType<typeof useCombobox>) =>
  useKeyboardNavigation({
    getItemValues: () => combobox.filteredItems.value,
    getHighlighted: () => {
      const h = combobox.highlightedValue.value;
      if (h !== undefined) return h;
      const s = combobox.modelValue.value;
      return s !== undefined && !Array.isArray(s) ? (s as AcceptableValue) : undefined;
    },
    setHighlighted: (v) => combobox.setHighlightedValue(v),
    scrollIntoView: (v) => combobox.scrollItemIntoView(v),
    onSelect: (v) => combobox.toggleSelection(v),
    onEscape: () => combobox.close(),
    onTab: () => {
      if (combobox.isOpen.value) combobox.close();
    },
  }).handleKeydown;

export const useComboboxInput = (options: UseComboboxInputOptions): UseComboboxInputReturn => {
  const { props } = options;
  const combobox = useCombobox();
  const host = useHost();
  const { isLeading, leadingIconName } = useIcons(props);
  const blurState = { timeoutId: null as ReturnType<typeof setTimeout> | null };

  const comboboxEl = combobox.comboboxElement.value;
  if (comboboxEl && host) registerComboboxInputElement(comboboxEl, host);

  const computeds = buildInputComputeds(props, combobox);
  const removeTag = (value: AcceptableValue) => {
    if (combobox.comboboxProps.value?.disabled) return;
    combobox.deselect(value);
  };
  const handlers = buildComboboxInputHandlers(combobox, blurState);
  const navKeydown = buildNavKeydown(combobox);
  const handleKeydown = (event: KeyboardEvent) =>
    handleComboboxKeydown(event, combobox, computeds.isMultiple, computeds.tags, removeTag, navKeydown);

  onBeforeUnmount(() => {
    if (blurState.timeoutId) clearTimeout(blurState.timeoutId);
    const el = combobox.comboboxElement.value;
    if (el) unregisterComboboxInputElement(el);
  });

  const sortable = computed(() => combobox.comboboxProps.value?.sortable);
  const { tagBindings, containerBindings: reorderContainerBindings } = useTagReorder({
    sortable,
    tags: computeds.tags,
    onReorder: (next) => combobox.setModelValue(next),
  });

  return {
    isLeading,
    leadingIconName,
    ...computeds,
    removeTag,
    combobox,
    tagBindings,
    reorderContainerBindings,
    getTagDisplayValue: (value: AcceptableValue) => resolveTagDisplayValue(value, props, combobox),
    ...handlers,
    handleKeydown,
  };
};
